import type { SubmissionScope } from './PageDto';
import { ReviewStatus } from './ReviewStatus';
import { FormType } from './FormType';
import { FormEntryStatus } from './FormEntryDto';
import { Dictionary } from '../../../core';
import { addArrayParams, addDictionaryParams } from '../helpers';
import { FormFieldDto } from './FormDto';

export enum FilteringType {
  ContainsAll = 'ContainsAll',
  ContainsAny = 'ContainsAny',
}

export enum FlagFilter {
  UnpaidItems = 'UnpaidItems',
  IsMissingRequiredFields = 'IsMissingRequiredFields',
  NotRegistered = 'NotRegistered',
  PendingPayment = 'PendingPayment',
  InConflict = 'InConflict',
}

export enum StatusFilter {
  Registered = 'Registered',
  Submitted = 'Submitted',
  OnWaitlist = 'OnWaitlist',
  Withdrawn = 'Withdrawn',
  Scheduled = 'Scheduled',
}

export enum FileFilter {
  WithFile = 'WithFile',
  WithoutFile = 'WithoutFile',
}

export interface GetFormEntriesModel {
  formId: string;
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  sortField?: string;
  sortDirection?: 'ascending' | 'descending';
  thenBySortField?: string;
  authorUserId?: string;
  userId?: string;
  subsetIds?: string[];
  excludedIds?: string[];
  filterChoices?: Dictionary<string[]>;
  unassignedFormFieldIds?: string[];
  assignedFormFieldIds?: string[];
  files?: Dictionary<FileFilter>;
  reviewStatusFilters?: string[];
  filterCurrentUserReviews?: boolean;
  flagFilters?: string[];
  statusFilters?: StatusFilter[];
  withSubmissionFilters?: string[] | boolean[];
  submissionDecisionsFilter?: string[];
  sessionIds?: string[];
  submissionDecisionsFilteringType?: FilteringType;
  filteringType?: FilteringType;
  isPublishedFilter?: boolean;
  from?: string | null;
  to?: string | null;
  lastUpdateDateFrom?: string | null;
  lastUpdateDateTo?: string | null;
  submissionScope?: SubmissionScope | null;
  isPresentingNow?: boolean;
  isBookmarked?: boolean;
  isVirtualPoster?: boolean;
  isOnline?: boolean;
  countryRegions?: Dictionary<string[]>;
  stateProvinces?: Dictionary<string[]>;
}

export type MetaDataFilter = {
  label: string;
  typeLabel: string;
  value: ReviewStatus | FlagFilter | StatusFilter;
  types: FormType[];
};

export const ReviewStatusFilters: MetaDataFilter[] = [
  {
    typeLabel: 'events.tables.review_status',
    value: ReviewStatus.Unassigned,
    label: 'events.reviews.statuses.unassigned',
    types: [FormType.Review],
  },
  {
    typeLabel: 'events.tables.review_status',
    value: ReviewStatus.Pending,
    label: 'events.reviews.statuses.pending',
    types: [FormType.Review],
  },
  {
    typeLabel: 'events.tables.review_status',
    value: ReviewStatus.ConflictOfInterest,
    label: 'events.reviews.statuses.conflictofinterest',
    types: [FormType.Review],
  },
  {
    typeLabel: 'events.tables.review_status',
    value: ReviewStatus.Completed,
    label: 'events.reviews.statuses.completed',
    types: [FormType.Review],
  },
];

export const FlagFilters: MetaDataFilter[] = [
  {
    typeLabel: 'events.tables.flags',
    value: FlagFilter.UnpaidItems,
    label: 'events.tables.flags_items.unpaid_items',
    types: [FormType.Registration, FormType.Submission],
  },
  {
    typeLabel: 'events.tables.flags',
    value: FlagFilter.IsMissingRequiredFields,
    label: 'events.tables.flags_items.is_missing_required_fields',
    types: [FormType.Registration, FormType.Submission],
  },
  {
    typeLabel: 'events.tables.flags',
    value: FlagFilter.NotRegistered,
    label: 'events.tables.flags_items.not_registered',
    types: [FormType.Submission],
  },
  {
    typeLabel: 'events.tables.flags',
    value: FlagFilter.PendingPayment,
    label: 'events.tables.flags_items.pending_payment',
    types: [FormType.Registration, FormType.Submission],
  },
  {
    typeLabel: 'events.tables.flags',
    value: FlagFilter.InConflict,
    label: 'events.tables.flags_items.conflict',
    types: [FormType.Submission],
  },
];

export const StatusFilters: MetaDataFilter[] = [
  {
    typeLabel: 'events.tables.status',
    value: StatusFilter.Registered,
    label: `events.tables.status_items.${FormEntryStatus.Registered}`,
    types: [FormType.Registration],
  },
  {
    typeLabel: 'events.tables.status',
    value: StatusFilter.Submitted,
    label: `events.tables.status_items.${FormEntryStatus.Submitted}`,
    types: [FormType.Submission],
  },
  {
    typeLabel: 'events.tables.status',
    value: StatusFilter.OnWaitlist,
    label: `events.tables.status_items.${FormEntryStatus.OnWaitingList}`,
    types: [FormType.Registration],
  },
  {
    typeLabel: 'events.tables.status',
    value: StatusFilter.Withdrawn,
    label: `events.tables.status_items.${FormEntryStatus.Withdrawn}`,
    types: [FormType.Registration, FormType.Submission],
  },
  {
    typeLabel: 'events.tables.status',
    value: StatusFilter.Scheduled,
    label: `events.tables.status_items.${FormEntryStatus.Scheduled}`,
    types: [FormType.Submission],
  },
];

export const MetaDataFilters = ReviewStatusFilters.concat(FlagFilters);

export function toURLSearchParams(model: GetFormEntriesModel): URLSearchParams {
  const query = new URLSearchParams(model as any);

  // URLSearchParams do not handles array, ex: foo=bar1&foo=bar2
  addDictionaryParams(query, 'filterChoices', model.filterChoices);
  addArrayParams(query, 'assignedFormFieldIds', model.assignedFormFieldIds);
  addArrayParams(query, 'unassignedFormFieldIds', model.unassignedFormFieldIds);
  addArrayParams(query, 'reviewStatusFilters', model.reviewStatusFilters);
  addArrayParams(query, 'flagFilters', model.flagFilters);
  addArrayParams(query, 'statusFilters', model.statusFilters);
  addArrayParams(query, 'withSubmissionFilters', model.withSubmissionFilters);
  addArrayParams(query, 'sessionIds', model.sessionIds);
  addArrayParams(query, 'submissionDecisionsFilter', model.submissionDecisionsFilter);
  addArrayParams(query, 'subsetIds', model.subsetIds);
  addArrayParams(query, 'excludedIds', model.excludedIds);
  return query;
}

export function buildFilterChoicesDictionary(
  filterChoices: string[],
  formFields: FormFieldDto[],
): Dictionary<string[]> {
  const dictionary: Dictionary<string[]> = {};

  filterChoices.forEach(fieldOrChoiceId => {
    const isField = fieldOrChoiceId.startsWith('.');
    if (isField) {
      const fieldId = fieldOrChoiceId.slice(1);
      dictionary[fieldId] = dictionary[fieldId] || [];
      dictionary[fieldId].push('');
    } else {
      const choiceId = fieldOrChoiceId;
      const field = formFields.find(field => field.formFieldChoices.some(choice => choice.id === choiceId));
      if (field) {
        dictionary[field.id] = dictionary[field.id] || [];
        dictionary[field.id].push(choiceId);
      }
    }
  });
  return dictionary;
}
